import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getArticleById, formatHeat, getRelatedArticles } from "@/utils/gossip";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Seo from "@/components/feature/Seo";
import ArticleSidebar from "@/pages/article/components/ArticleSidebar";

function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const value = total > 0 ? (window.scrollY / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, value)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-transparent">
      <div
        className="h-full bg-primary-500 transition-[width] duration-150"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
}

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          这条瓜料暂时找不到了
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          可能是链接有误，或者该内容已经调整。可以先回到猛料总汇看看最新的瓜。
        </p>
        <Link
          to="/hot"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-label font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回猛料总汇
        </Link>
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: [article.cover],
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: `${article.author} · 看料吃瓜社` },
      publisher: { "@type": "Organization", name: "看料吃瓜社" },
      articleSection: article.categoryLabel,
      keywords: article.tags.join(","),
      mainEntityOfPage: `https://kanliaochigua.example/hot/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliaochigua.example/" },
        { "@type": "ListItem", position: 2, name: "猛料总汇", item: "https://kanliaochigua.example/hot" },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `https://kanliaochigua.example/hot/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | 看料吃瓜社`}
        description={`${article.excerpt} 内容线：${article.categoryLabel}，整理时间 ${article.date}。`}
        keywords={`${article.categoryLabel},${article.tags.join(",")},看料吃瓜,看料吃瓜猛料`}
        path={`/hot/${article.id}`}
        image={article.cover}
        jsonLd={jsonLd}
      />
      <ReadingProgress />

      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "猛料总汇", to: "/hot" },
              { label: article.categoryLabel, to: `/hot?category=${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link
              to={`/hot?category=${article.category}`}
              className="text-[11px] font-label text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full hover:bg-primary-200 transition-colors"
            >
              {article.categoryLabel}
            </Link>
            {article.hot && (
              <span className="text-[11px] font-label text-accent-900 bg-accent-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                <i className="ri-fire-line"></i>
                热门大瓜
              </span>
            )}
            {article.deep && (
              <span className="text-[11px] font-label text-secondary-900 bg-secondary-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                <i className="ri-focus-3-line"></i>
                深水硬核
              </span>
            )}
          </div>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-4xl">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-label text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              整理于 {article.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读约 {article.readTime}
            </span>
            <span className="inline-flex items-center gap-1.5 text-primary-600">
              <i className="ri-fire-line"></i>
              讨论热度 {formatHeat(article.heat)}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className={copied ? "ri-check-line" : "ri-link"}></i>
              {copied ? "链接已复制" : "复制链接"}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <article className="lg:col-span-8 min-w-0">
            <figure className="w-full h-56 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.cover}
                alt={`${article.title}配图`}
                title={`${article.title} - 看料吃瓜社`}
                className="w-full h-full object-cover"
              />
            </figure>

            <section id="overview" className="scroll-mt-28 mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                事件概览
              </h2>
              <p className="p-5 bg-accent-50 border-l-4 border-accent-500 rounded-r-md text-sm md:text-base text-foreground-700 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/hot?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 text-xs font-label rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>

            <section id="timeline" className="scroll-mt-28 mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                时间线梳理
              </h2>
              <ol className="relative pl-6 border-l border-background-300">
                {article.timeline.map((step, index) => (
                  <li key={step.time} className={index === article.timeline.length - 1 ? "" : "pb-6"}>
                    <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-background-50"></span>
                    <p className="font-label text-sm font-medium text-primary-700">{step.time}</p>
                    <p className="mt-1 text-sm md:text-base text-foreground-700 leading-relaxed">
                      {step.event}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="content" className="scroll-mt-28 mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                正文整理
              </h2>
              <div className="space-y-5">
                {article.content.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 24)}
                    className="text-sm md:text-base text-foreground-700 leading-loose"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section id="sources" className="scroll-mt-28">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                信息来源与说明
              </h2>
              <div className="p-5 bg-background-100 border border-background-200 rounded-lg">
                <ul className="space-y-2.5">
                  {article.sources.map((source) => (
                    <li key={source} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                      {source}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 pt-4 border-t border-background-200 text-xs text-foreground-500 leading-relaxed">
                  本文为公开信息整理与再叙述，不含针对任何个人的主观定性。如发现内容存在偏差，欢迎通过
                  <Link to="/contact" className="text-primary-700 hover:text-primary-800 mx-1">
                    联系表单
                  </Link>
                  提交更正说明。
                </p>
              </div>
            </section>
          </article>

          <div className="lg:col-span-4">
            <ArticleSidebar article={article} />
          </div>
        </div>

        <section className="mt-14 md:mt-20 pt-10 border-t border-background-200" aria-labelledby="related-heading">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 id="related-heading" className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              继续看看这些瓜料
            </h2>
            <Link
              to="/hot"
              className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              全部猛料
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}